import { Strategy as StrategyType } from '../utils'
import Styles from './CustomizeTable.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { CountInput } from 'components/Wizards/CountInput/CountInput'
import cx from 'classnames'
import { useHover } from '@react-aria/interactions'
import { Trash2 } from 'blaze-ui/icons/20'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { EmailPlatformSelect } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { useEffect, useState } from 'react'
import { BrandPlanTactic, SelectedEmailPlatform } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Email } from 'blaze-ui/icons/24'
import { uniq } from 'lodash'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'

export interface NewsletterRowProps {
  strategy: StrategyType
  onUpdate: (strategy: StrategyType) => void
  selectedEmailPlatform: SelectedEmailPlatform
  setSelectedEmailPlatform: (platform: SelectedEmailPlatform) => void
  isDisabled?: boolean
}

export const NewsletterRow = ({
  strategy,
  onUpdate,
  selectedEmailPlatform,
  setSelectedEmailPlatform,
  isDisabled,
}: NewsletterRowProps) => {
  const [isEmailSelected, setIsEmailSelected] = useState<boolean>(true)
  const { isMobile } = useResponsiveDesign()
  const { hoverProps, isHovered } = useHover({})
  const emailContentsCount = strategy.email_contents.find((content) => content.type === 'newsletter')?.count || 0

  useEffect(() => {
    if (emailContentsCount > 0 && !isEmailSelected) {
      setIsEmailSelected(true)
      setSelectedEmailPlatform(ExternalIntegrationEnum.Mailchimp)
    }
  }, [emailContentsCount, isEmailSelected, setSelectedEmailPlatform])

  return (
    <div className={Styles.customizeContentRow} {...hoverProps}>
      <div className={Styles.customizeContentRowItem}>
        <Email size={20} color="rgba(255, 174, 0, 1)" />
        {isMobile ? <Heading level={5}>Email</Heading> : <Text>Email</Text>}
      </div>
      <div
        className={cx(Styles.customizeContentRowItem, Styles.settingRowItem, {
          [Styles.disabled]: emailContentsCount === 0,
        })}
      >
        {isMobile && <Text>Channels</Text>}
        <EmailPlatformSelect
          isSelected={isEmailSelected}
          selectedPlatform={selectedEmailPlatform}
          onSelectionChange={(selected) => {
            if (!selected) {
              onUpdate({
                ...strategy,
                email_contents: [{ type: 'newsletter', count: 0 }],
                tactics: strategy.tactics.filter((tactic) => tactic !== BrandPlanTactic.Email),
              })
            }
            setIsEmailSelected(selected)
          }}
          onPlatformChange={setSelectedEmailPlatform}
          showOnlyDropdown
          size="md"
          isDisabled={emailContentsCount === 0}
          allowNone={!isDisabled}
        />
      </div>
      <div className={cx(Styles.customizeContentRowItem, Styles.settingRowItem)}>
        {isMobile && <Text>Posts per week</Text>}
        <CountInput
          name="email_contents"
          value={emailContentsCount || 0}
          onChange={(value) => {
            onUpdate({
              ...strategy,
              email_contents: [{ type: 'newsletter', count: value }],
              tactics:
                value > 0
                  ? uniq([...strategy.tactics, BrandPlanTactic.Email])
                  : strategy.tactics.filter((tactic) => tactic !== BrandPlanTactic.Email),
            })
          }}
          min={isDisabled ? 1 : 0}
          size="sm"
          buttonSize="sm"
          className={Styles.countInput}
        />
      </div>
      {!isMobile && (
        <div
          className={cx(Styles.customizeContentRowItem, Styles.actionRowItem, {
            [Styles.visible]: isHovered && emailContentsCount > 0,
          })}
        >
          <IconButton
            icon={Trash2}
            variant="tertiary"
            isDisabled={isDisabled}
            onPress={() => {
              onUpdate({
                ...strategy,
                email_contents: [{ type: 'newsletter', count: 0 }],
              })
            }}
          />
        </div>
      )}
    </div>
  )
}
